<template>
  <VaLayout
    :top="{ fixed: true, order: 2 }"
    :left="{ fixed: true, absolute: breakpoints.mdDown, order: 1, overlay: breakpoints.mdDown && !isSidebarMinimized }"
    @leftOverlayClick="isSidebarMinimized = true"
  >
    <template #top>
      <AppNavbar :is-mobile="isMobile" />
    </template>

    <template #left>
      <AppSidebar
        :minimized="isSidebarMinimized"
        :animated="!isMobile"
        :mobile="isMobile"
        @expand="isSidebarMinimized = !isSidebarMinimized"
      />
    </template>

    <template #content>
      <div :class="{ minimized: isSidebarMinimized }" class="app-layout__sidebar-wrapper">
        <div v-if="isFullScreenSidebar" class="flex justify-end">
          <VaButton class="px-4 py-4" icon="md_close" preset="plain" @click="onCloseSidebarButtonClick" />
        </div>
      </div>
      <AppLayoutNavigation v-if="!isMobile" class="px-0 py-1" />
      <main class="px-0 pt-0 pb-1">
        <RouterView />
      </main>
      <!-- Global Success Modal that can be triggered from anywhere -->
      <GlobalSuccessModal />
    </template>
  </VaLayout>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { onBeforeRouteUpdate } from 'vue-router'
import { useBreakpoint, useColors } from 'vuestic-ui'

import { useGlobalStore } from '../stores/global-store'

import AppLayoutNavigation from '../components/app-layout-navigation/AppLayoutNavigation.vue'
import AppNavbar from '../components/navbar/AppNavbar.vue'
import AppSidebar from '../components/sidebar/AppSidebar.vue'
import GlobalSuccessModal from '../components/GlobalSuccessModal.vue'

const GlobalStore = useGlobalStore()
const { applyPreset } = useColors()

const breakpoints = useBreakpoint()

const sidebarWidth = ref('16rem')
const sidebarMinimizedWidth = ref(undefined)

const isMobile = ref(false)
const isTablet = ref(false)
const { isSidebarMinimized } = storeToRefs(GlobalStore)

const onResize = () => {
  isSidebarMinimized.value = breakpoints.mdDown
  isMobile.value = breakpoints.smDown
  isTablet.value = breakpoints.mdDown
  sidebarMinimizedWidth.value = isMobile.value ? '0' : '4.5rem'
  sidebarWidth.value = isTablet.value ? '100%' : '16rem'
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  onResize()

  // Initialize theme from localStorage — key 'theme' matches the toggle in AppNavbarActions
  const savedTheme = localStorage.getItem('theme') || 'dark'
  applyPreset(savedTheme)
  document.documentElement.classList.toggle('dark', savedTheme === 'dark')
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

onBeforeRouteUpdate(() => {
  if (breakpoints.mdDown) {
    // Collapse sidebar after route change for Mobile
    isSidebarMinimized.value = true
  }
})

const isFullScreenSidebar = computed(() => isTablet.value && !isSidebarMinimized.value)

const onCloseSidebarButtonClick = () => {
  isSidebarMinimized.value = true
}
</script>

<style lang="scss" scoped>
// Prevent icon jump on animation
.va-sidebar {
  width: unset !important;
  min-width: unset !important;
}

// Remove default VaLayout content padding for tight layout
:deep(.va-layout__content) {
  padding: 0 !important;
}

// Remove ALL gaps from VaLayout
:deep(.va-layout) {
  gap: 0 !important;
  column-gap: 0 !important;
  row-gap: 0 !important;
}

// Remove any VaLayout left area gap/margin
:deep(.va-layout__area--left) {
  margin-right: 0 !important;
  margin-inline-end: 0 !important;
}

// Ensure content area has zero gap
:deep(.va-layout__area--content) {
  padding: 0 !important;
  margin: 0 !important;
}

// Ensure main content uses full available width
main {
  width: 100%;
  max-width: none;
}
</style>
